// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { appStore } from '@advisor-app/store/store';
import { useSafeUpdate } from '@shared/ui-kit/hooks';
import { useCallback, useEffect, useState } from 'react';
import { useAppSelector } from '@advisor-app/store/store';
import { accountApi } from '@advisor-app/store/api/brokerage/accounts-api';
import {
  selectAccount,
  selectBrokerConfiguration,
  selectBrokerConnected,
} from '@advisor-app/store/slices/broker';
import { getAssetsInfo } from '@shared/data-access/transformers/assets/get-assets-info';

const sideMap: Record<string, 'BUY' | 'SELL'> = {
  SELL: 'SELL',
  BUY: 'BUY',
  SELL_SHORT: 'SELL',
};

export const useTradesHistory = () => {
  const runSafeUpdate = useSafeUpdate();

  const [tradesHistory, setTradesHistory] = useState<any[]>([]);
  const isBrokerConnected = useAppSelector(selectBrokerConnected);
  const account = useAppSelector(selectAccount);
  const brokerConf = useAppSelector(selectBrokerConfiguration);

  const [getAccountTransaction] = accountApi.useLazyAccountTransactionQuery();

  const getTradesInformation = useCallback(async (tickers: string[]) => {
    const assetsInformation = await getAssetsInfo(tickers, appStore.dispatch);
    return assetsInformation;
  }, []);

  const getTradesHistory = useCallback(async () => {
    try {
      const payload = { account_id: account.accountId };
      const accountTransactionResponse = await getAccountTransaction({
        params: brokerConf,
        payload,
      });
      /* istanbul ignore next */
      if (accountTransactionResponse.isError)
        throw accountTransactionResponse.error;

      const tickers = accountTransactionResponse
        .data!.map(item => item.symbol)
        .filter(Boolean);

      const tradesInfo = await getTradesInformation(tickers);

      const updatedData = accountTransactionResponse
        .data!.filter(value => Boolean(value.symbol))
        .map(item => {
          const asset = tradesInfo.find(
            asset => asset.ticker === item.symbol,
          ) as any;

          return {
            ...item,
            isin: asset?.isin,
            name: asset?.name,
            logo: asset?.url_logo,
            price: 1,
            date: new Date(item.date),
            side: sideMap[item.type],
            asset_type: asset?.asset_type,
            url_morningstar: asset?.url_morningstar,
          };
        });

      runSafeUpdate(() => {
        setTradesHistory(updatedData);
      });
    } catch (error) {
      /* istanbul ignore next */
      console.error('[getTradesHistory]', error);
    }
  }, [
    account.accountId,
    brokerConf,
    getAccountTransaction,
    runSafeUpdate,
    getTradesInformation,
  ]);

  useEffect(() => {
    if (isBrokerConnected) {
      getTradesHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBrokerConnected]);

  return { tradesHistory };
};
